import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

const INSIDER_URL =
  "https://raw.githubusercontent.com/aport550/majorah-data/main/public/data/insider_trading.json";

const STOCK_SCREENER_URL =
  "https://raw.githubusercontent.com/aport550/majorah-data/main/public/data/stock_screener.json";

const STOCK_DETAIL_PATH = "/stocks2";

const DEFAULT_FILTERS = {
  search: "",
  side: "ALL",
  informative: "ALL",
  minValue: "",
  maxValue: "",
  minMarketCap: "",
  maxMarketCap: "",
  relationship: "ALL",
  dateFrom: "",
  dateTo: "",
};

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth <= breakpoint : false
  );

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);

  return isMobile;
}

function parseNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(String(value).replace(/[$,%]/g, "").replace(/,/g, ""));
  return Number.isFinite(n) ? n : null;
}

function normalizeTicker(value) {
  return String(value || "").trim().toUpperCase();
}

function normalizePayload(json) {
  if (Array.isArray(json)) return json;
  if (Array.isArray(json?.data)) return json.data;
  if (Array.isArray(json?.rows)) return json.rows;
  return [];
}

function normalizeScreenerPayload(json) {
  if (Array.isArray(json)) return json;
  if (Array.isArray(json?.data)) return json.data;
  if (Array.isArray(json?.rows)) return json.rows;
  return [];
}

function formatLargeMoney(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  const abs = Math.abs(n);

  if (abs >= 1_000_000_000_000) return `$${(n / 1_000_000_000_000).toFixed(2)}T`;
  if (abs >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(2)}B`;
  if (abs >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `$${(n / 1_000).toFixed(2)}K`;
  return `$${n.toFixed(0)}`;
}

function formatMoney(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return `$${n.toFixed(2)}`;
}

function formatNumber(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString();
}

function formatPercent(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return `${(n * 100).toFixed(1)}%`;
}

function daysSince(dateString) {
  if (!dateString) return null;
  const dt = new Date(`${dateString}T00:00:00`);
  if (Number.isNaN(dt.getTime())) return null;

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const then = new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());

  return Math.floor((today.getTime() - then.getTime()) / 86400000);
}

function getRoleWeight(relationship) {
  const r = String(relationship || "").toLowerCase();

  if (r.includes("chief executive") || r.includes("ceo")) return 1.45;
  if (r.includes("chief financial") || r.includes("cfo")) return 1.35;
  if (r.includes("president")) return 1.25;
  if (r.includes("officer")) return 1.15;
  if (r.includes("director")) return 1.05;
  if (r.includes("10%")) return 0.85;

  return 1;
}

function getInsiderConvictionScore(row) {
  const value = Number(row.value);
  if (!Number.isFinite(value) || value <= 0) return null;

  const side = String(row.transaction_side || "").toLowerCase();
  const sideMultiplier = side === "buy" ? 1 : side === "sale" ? -1 : 0;
  if (sideMultiplier === 0) return null;

  const valueScore = Math.min(100, Math.log10(Math.max(10, value)) * 14);
  const roleScore = getRoleWeight(row.relationship);
  const informativeBoost = row.informative ? 1.35 : 0.8;

  return sideMultiplier * valueScore * roleScore * informativeBoost;
}

function getDateMs(value) {
  if (!value) return null;
  const dt = new Date(value);
  const ms = dt.getTime();
  return Number.isFinite(ms) ? ms : null;
}

function compareValues(a, b, direction = "desc") {
  const dir = direction === "asc" ? 1 : -1;
  const aMissing = a === null || a === undefined || a === "" || Number.isNaN(a);
  const bMissing = b === null || b === undefined || b === "" || Number.isNaN(b);

  if (aMissing && bMissing) return 0;
  if (aMissing) return 1;
  if (bMissing) return -1;

  const an = Number(a);
  const bn = Number(b);

  if (Number.isFinite(an) && Number.isFinite(bn)) {
    return (an - bn) * dir;
  }

  const ad = getDateMs(a);
  const bd = getDateMs(b);

  if (Number.isFinite(ad) && Number.isFinite(bd)) {
    return (ad - bd) * dir;
  }

  return String(a).localeCompare(String(b)) * dir;
}

function getSortValue(row, key) {
  if (key === "days_since") return row.days_since;
  if (key === "conviction_score_abs") return Math.abs(row.conviction_score ?? 0);
  return row[key];
}

function passesDateRange(dateString, from, to) {
  if (!from && !to) return true;
  if (!dateString) return false;

  const t = getDateMs(`${dateString}T00:00:00`);
  if (!Number.isFinite(t)) return false;

  if (from) {
    const fromMs = getDateMs(`${from}T00:00:00`);
    if (Number.isFinite(fromMs) && t < fromMs) return false;
  }

  if (to) {
    const toMs = getDateMs(`${to}T23:59:59`);
    if (Number.isFinite(toMs) && t > toMs) return false;
  }

  return true;
}

function SidePill({ side }) {
  const s = String(side || "").toLowerCase();
  const isBuy = s === "buy";
  const isSale = s === "sale";

  return (
    <span style={{ ...pillBase, ...(isBuy ? buyPill : isSale ? salePill : neutralPill) }}>
      {side || "—"}
    </span>
  );
}

function InformativePill({ informative }) {
  return (
    <span style={{ ...pillBase, ...(informative ? informativePill : uninformativePill) }}>
      {informative ? "Informative" : "Uninformative"}
    </span>
  );
}

function ScorePill({ value }) {
  const n = Number(value);
  if (!Number.isFinite(n)) return <span style={{ color: "#94a3b8" }}>—</span>;

  return (
    <span style={{ ...pillBase, ...(n >= 0 ? buyPill : salePill) }}>
      {Math.round(n)}
    </span>
  );
}

const desktopColumns = [
  { key: "transaction_date", label: "Date", width: 95, sticky: true, left: 0 },
  { key: "ticker", label: "Ticker", width: 88, sticky: true, left: 95 },
  { key: "transaction_side", label: "Side", width: 82 },
  { key: "informative", label: "Signal", width: 116 },
  { key: "value", label: "Value", type: "largeMoney", align: "right", width: 105 },
  { key: "conviction_score_abs", label: "Score", align: "right", width: 78 },
  { key: "owner", label: "Owner", width: 210 },
  { key: "relationship", label: "Relationship", width: 160 },
  { key: "transaction", label: "Transaction", width: 150 },
  { key: "cost", label: "Cost", type: "money", align: "right", width: 82 },
  { key: "shares", label: "Shares", type: "number", align: "right", width: 100 },
  { key: "shares_total", label: "Shares Total", type: "number", align: "right", width: 120 },
  { key: "days_since", label: "Days Ago", align: "right", width: 85 },
  { key: "marketCap", label: "Market Cap", type: "largeMoney", align: "right", width: 112 },
  { key: "price", label: "Price", type: "money", align: "right", width: 82 },
  { key: "forwardPE", label: "Fwd P/E", type: "ratio", align: "right", width: 84 },
  { key: "pb", label: "P/B", type: "ratio", align: "right", width: 70 },
  { key: "rsi14", label: "RSI", type: "ratio", align: "right", width: 70 },
  { key: "pctAbove52WeekLow", label: "% Above Low", type: "percent", align: "right", width: 108 },
  { key: "sec_form_4_datetime", label: "SEC Form 4", width: 145 },
];

const mobileColumns = [
  { key: "transaction_date", label: "Date", width: 88, sticky: true, left: 0 },
  { key: "ticker", label: "Ticker", width: 76, sticky: true, left: 88 },
  { key: "transaction_side", label: "Side", width: 74 },
  { key: "informative", label: "Signal", width: 106 },
  { key: "value", label: "Value", type: "largeMoney", align: "right", width: 96 },
  { key: "conviction_score_abs", label: "Score", align: "right", width: 70 },
  { key: "owner", label: "Owner", width: 150 },
  { key: "relationship", label: "Role", width: 125 },
  { key: "days_since", label: "Days", align: "right", width: 66 },
  { key: "marketCap", label: "Mkt Cap", type: "largeMoney", align: "right", width: 98 },
];

export default function InsiderTradingPage() {
  const isMobile = useIsMobile(768);

  const [records, setRecords] = useState([]);
  const [screenerRows, setScreenerRows] = useState([]);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [historyCount, setHistoryCount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [sortKey, setSortKey] = useState("transaction_date");
  const [sortDir, setSortDir] = useState("desc");

  const visibleColumns = isMobile ? mobileColumns : desktopColumns;

  useEffect(() => {
    let alive = true;

    async function load() {
      try {
        setLoading(true);
        setError("");

        const [insiderRes, screenerRes] = await Promise.all([
          fetch(INSIDER_URL, { cache: "no-store" }),
          fetch(STOCK_SCREENER_URL, { cache: "no-store" }),
        ]);

        const [insiderText, screenerText] = await Promise.all([
          insiderRes.text(),
          screenerRes.text(),
        ]);

        if (!insiderRes.ok || insiderText.trim().startsWith("<")) {
          throw new Error(`Could not load insider_trading.json. First 120 chars: ${insiderText.slice(0, 120)}`);
        }

        if (!screenerRes.ok || screenerText.trim().startsWith("<")) {
          throw new Error(`Could not load stock_screener.json. First 120 chars: ${screenerText.slice(0, 120)}`);
        }

        const insiderJson = JSON.parse(insiderText);
        const screenerJson = JSON.parse(screenerText);

        if (alive) {
          setRecords(normalizePayload(insiderJson));
          setGeneratedAt(insiderJson?.generated_at || null);
          setHistoryCount(insiderJson?.history_record_count ?? insiderJson?.record_count ?? null);
          setScreenerRows(normalizeScreenerPayload(screenerJson));
        }
      } catch (err) {
        console.error(err);
        if (alive) setError(err?.message || "Failed to load insider trading data.");
      } finally {
        if (alive) setLoading(false);
      }
    }

    load();

    return () => {
      alive = false;
    };
  }, []);

  const screenerByTicker = useMemo(() => {
    const map = new Map();

    for (const row of screenerRows) {
      const ticker = normalizeTicker(row?.ticker);
      if (!ticker) continue;
      map.set(ticker, row);
    }

    return map;
  }, [screenerRows]);

  const enrichedRows = useMemo(() => {
    return records.map((record) => {
      const ticker = normalizeTicker(record.ticker);
      const screener = screenerByTicker.get(ticker) || {};

      return {
        ...record,
        ticker,
        days_since: daysSince(record.transaction_date),
        conviction_score: getInsiderConvictionScore(record),
        marketCap: parseNumber(screener.marketCap),
        price: parseNumber(screener.price),
        forwardPE: parseNumber(screener.forwardPE),
        pb: parseNumber(screener.pb),
        peg: parseNumber(screener.peg),
        rsi14: parseNumber(screener.rsi14),
        pctAbove52WeekLow: parseNumber(screener.pctAbove52WeekLow),
      };
    });
  }, [records, screenerByTicker]);

  const relationships = useMemo(() => {
    return Array.from(
      new Set(enrichedRows.map((x) => String(x.relationship || "").trim()).filter(Boolean))
    ).sort((a, b) => a.localeCompare(b));
  }, [enrichedRows]);

  // const summary = useMemo(() => {
  //   const buys = enrichedRows.filter((x) => x.transaction_side === "Buy");
  //   const sales = enrichedRows.filter((x) => x.transaction_side === "Sale");
  //   const informative = enrichedRows.filter((x) => x.informative);
  //   const buyValue = buys.reduce((sum, x) => sum + (Number(x.value) || 0), 0);
  //   const saleValue = sales.reduce((sum, x) => sum + (Number(x.value) || 0), 0);

  //   return {
  //     total: enrichedRows.length,
  //     buys: buys.length,
  //     sales: sales.length,
  //     informative: informative.length,
  //     buyValue,
  //     saleValue,
  //   };
  // }, [enrichedRows]);

  const filteredRows = useMemo(() => {
    const q = filters.search.trim().toLowerCase();

    const data = enrichedRows.filter((row) => {
      const ticker = String(row.ticker || "").toLowerCase();
      const owner = String(row.owner || "").toLowerCase();
      const relationship = String(row.relationship || "").toLowerCase();
      const transaction = String(row.transaction || "").toLowerCase();

      const matchesSearch =
        !q ||
        ticker.includes(q) ||
        owner.includes(q) ||
        relationship.includes(q) ||
        transaction.includes(q);

      const matchesSide =
        filters.side === "ALL" || row.transaction_side === filters.side;

      const matchesInformative =
        filters.informative === "ALL" ||
        (filters.informative === "INFORMATIVE" && row.informative) ||
        (filters.informative === "UNINFORMATIVE" && !row.informative);

      const matchesRelationship =
        filters.relationship === "ALL" || row.relationship === filters.relationship;

      const value = Number(row.value);
      const marketCap = Number(row.marketCap);

      const matchesMinValue =
        !filters.minValue || (Number.isFinite(value) && value >= Number(filters.minValue));

      const matchesMaxValue =
        !filters.maxValue || (Number.isFinite(value) && value <= Number(filters.maxValue));

      const matchesMinMarketCap =
        !filters.minMarketCap ||
        (Number.isFinite(marketCap) && marketCap >= Number(filters.minMarketCap) * 1_000_000_000);

      const matchesMaxMarketCap =
        !filters.maxMarketCap ||
        (Number.isFinite(marketCap) && marketCap <= Number(filters.maxMarketCap) * 1_000_000_000);

      const matchesDate = passesDateRange(row.transaction_date, filters.dateFrom, filters.dateTo);

      return (
        matchesSearch &&
        matchesSide &&
        matchesInformative &&
        matchesRelationship &&
        matchesMinValue &&
        matchesMaxValue &&
        matchesMinMarketCap &&
        matchesMaxMarketCap &&
        matchesDate
      );
    });

    data.sort((a, b) =>
      compareValues(getSortValue(a, sortKey), getSortValue(b, sortKey), sortDir)
    );

    return data;
  }, [enrichedRows, filters, sortKey, sortDir]);

  function updateFilter(key, value) {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }

  function clearFilters() {
    setFilters(DEFAULT_FILTERS);
  }

  function handleSort(key) {
    if (sortKey === key) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  }

  function exportCsv() {
    const header = visibleColumns.map((c) => c.label).join(",");
    const body = filteredRows
      .map((row) =>
        visibleColumns
          .map((c) => {
            const value = row[c.key];
            return `"${value === null || value === undefined ? "" : String(value).replaceAll('"', '""')}"`;
          })
          .join(",")
      )
      .join("\n");

    const blob = new Blob([header + "\n" + body], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "majorah_insider_trading.csv";
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div style={pageShell}>
      <section style={heroCard}>
        <div style={headerRow}>
          <div>
            <div style={eyebrow}>Majorah Insider Intelligence</div>
            <h1 style={title}>Insider Buys & Sells</h1>
            <p style={subtitle}>
              {generatedAt ? ` Generated ${generatedAt}.` : ""}
              {historyCount}
            </p>
          </div>

          <div style={buttonRow}>
            <button onClick={clearFilters} style={secondaryButton}>
              Clear Filters
            </button>
            <button onClick={exportCsv} style={button}>
              Export CSV
            </button>
          </div>
        </div>

        <div style={metricGrid}>
{/*          <MetricCard label="Visible Rows" value={filteredRows.length.toLocaleString()} />
          <MetricCard label="History Rows" value={(historyCount ?? summary.total).toLocaleString()} />
          <MetricCard label="Buys" value={summary.buys.toLocaleString()} />
          <MetricCard label="Sales" value={summary.sales.toLocaleString()} />
          <MetricCard label="Informative" value={summary.informative.toLocaleString()} />
          <MetricCard label="Buy Value" value={formatLargeMoney(summary.buyValue)} />
          <MetricCard label="Sale Value" value={formatLargeMoney(summary.saleValue)} />
*/}        </div>
      </section>

      <section style={filterCard}>
        <div style={filterHeaderRow}>
          <div>
            <h2 style={sectionTitle}>Filters</h2>
            <p style={sectionSubtitle}>
              Filter by ticker, owner, buy/sell, informative status, size, role, and date.
            </p>
          </div>

          <div style={resultPill}>
            Showing <strong>{filteredRows.length}</strong> of <strong>{enrichedRows.length}</strong>
          </div>
        </div>

        <div style={controls}>
          <input
            value={filters.search}
            onChange={(e) => updateFilter("search", e.target.value)}
            placeholder="Search ticker, owner, role, transaction..."
            style={searchInput}
          />

          <select value={filters.side} onChange={(e) => updateFilter("side", e.target.value)} style={selectInput}>
            <option value="ALL">All Buy/Sell</option>
            <option value="Buy">Buys Only</option>
            <option value="Sale">Sales Only</option>
          </select>

          <select value={filters.informative} onChange={(e) => updateFilter("informative", e.target.value)} style={selectInput}>
            <option value="ALL">All Signals</option>
            <option value="INFORMATIVE">Informative Only</option>
            <option value="UNINFORMATIVE">Uninformative Only</option>
          </select>

          <select value={filters.relationship} onChange={(e) => updateFilter("relationship", e.target.value)} style={selectInput}>
            <option value="ALL">All Relationships</option>
            {relationships.map((relationship) => (
              <option key={relationship} value={relationship}>
                {relationship}
              </option>
            ))}
          </select>

          <FilterInput label="Min Value $" value={filters.minValue} onChange={(v) => updateFilter("minValue", v)} />
          <FilterInput label="Max Value $" value={filters.maxValue} onChange={(v) => updateFilter("maxValue", v)} />
          <FilterInput label="Min Mkt Cap $B" value={filters.minMarketCap} onChange={(v) => updateFilter("minMarketCap", v)} />
          <FilterInput label="Max Mkt Cap $B" value={filters.maxMarketCap} onChange={(v) => updateFilter("maxMarketCap", v)} />

          <input type="date" value={filters.dateFrom} onChange={(e) => updateFilter("dateFrom", e.target.value)} style={smallInput} />
          <input type="date" value={filters.dateTo} onChange={(e) => updateFilter("dateTo", e.target.value)} style={smallInput} />
        </div>
      </section>

      {loading ? (
        <div style={message}>Loading insider trading data...</div>
      ) : error ? (
        <div style={errorBox}>{error}</div>
      ) : (
        <section style={tableCard}>
          <div style={tableToolbar}>
            <div>
              <h2 style={sectionTitle}>Recent Insider Transactions</h2>
              <p style={sectionSubtitle}>Click a ticker to open its stock details page.</p>
            </div>
          </div>

          <div style={tableWrap}>
            <table style={{ ...table, minWidth: isMobile ? 980 : 2300 }}>
              <thead>
                <tr>
                  {visibleColumns.map((col) => (
                    <th key={col.key} onClick={() => handleSort(col.key)} style={getHeaderStyle(col)}>
                      {col.label}
                      {sortKey === col.key ? (
                        <span style={sortIndicator}>{sortDir === "asc" ? " ▲" : " ▼"}</span>
                      ) : null}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {filteredRows.map((row, index) => (
                  <tr
                    key={`${row.id || row.ticker}-${index}`}
                    style={{
                      ...(index % 2 === 0 ? rowEven : rowOdd),
                      ...(row.informative
                        ? row.transaction_side === "Buy"
                          ? informativeBuyRow
                          : informativeSaleRow
                        : {}),
                    }}
                  >
                    {visibleColumns.map((col) => (
                      <td key={col.key} style={getCellStyle(col)}>
                        {renderCell(row, col)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}

function FilterInput({ label, value, onChange }) {
  return (
    <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={label} type="number" style={smallInput} />
  );
}

// function MetricCard({ label, value }) {
//   return (
//     <div style={metricCard}>
//       <div style={metricLabel}>{label}</div>
//       <div style={metricValue}>{value}</div>
//     </div>
//   );
// }

function renderCell(row, col) {
  if (col.key === "ticker") {
    const ticker = normalizeTicker(row.ticker);
    if (!ticker) return "—";

    return (
      <Link to={`${STOCK_DETAIL_PATH}/${encodeURIComponent(ticker)}`} style={tickerLink}>
        {ticker}
      </Link>
    );
  }

  if (col.key === "transaction_side") return <SidePill side={row.transaction_side} />;
  if (col.key === "informative") return <InformativePill informative={row.informative} />;
  if (col.key === "conviction_score_abs") return <ScorePill value={row.conviction_score} />;

  if (col.key === "sec_form_4_datetime") {
    if (!row.sec_form_4_url) return row.sec_form_4_datetime || "—";

    return (
      <a href={row.sec_form_4_url} target="_blank" rel="noreferrer" style={secLink}>
        {row.sec_form_4_datetime || "SEC Form 4"}
      </a>
    );
  }

  return formatValue(row[col.key], col.type);
}

function formatValue(value, type) {
  if (value === null || value === undefined || value === "" || Number.isNaN(value)) return "—";
  const n = Number(value);
  if (type === "largeMoney") return formatLargeMoney(n);
  if (type === "money") return formatMoney(n);
  if (type === "number") return formatNumber(n);
  if (type === "percent") return formatPercent(n);
  if (type === "ratio") return Number.isFinite(n) ? n.toFixed(2) : "—";
  return String(value);
}

function getHeaderStyle(col) {
  const base = col.align === "right" ? thRight : thLeft;

  if (!col.sticky) {
    return { ...base, minWidth: col.width, width: col.width, maxWidth: col.width };
  }

  return {
    ...base,
    position: "sticky",
    left: col.left,
    minWidth: col.width,
    width: col.width,
    maxWidth: col.width,
    zIndex: 7,
    background: "#f8fafc",
    boxShadow: "2px 0 0 rgba(148, 163, 184, 0.18)",
  };
}

function getCellStyle(col) {
  const base = col.align === "right" ? tdRight : td;

  if (!col.sticky) {
    return { ...base, minWidth: col.width, width: col.width, maxWidth: col.width };
  }

  return {
    ...base,
    position: "sticky",
    left: col.left,
    minWidth: col.width,
    width: col.width,
    maxWidth: col.width,
    zIndex: 4,
    background: "#ffffff",
    boxShadow: "2px 0 0 rgba(226, 232, 240, 0.8)",
  };
}

const pageShell = {
  minHeight: "100vh",
  background: "linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%)",
  padding: "22px 10px 34px",
  fontFamily: "Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif",
  color: "#0f172a",
};

const heroCard = {
  maxWidth: 1680,
  margin: "0 auto 18px",
  padding: 20,
  background:
    "radial-gradient(circle at 16% 12%, rgba(34,197,94,0.22), transparent 34%), linear-gradient(135deg, #0f172a 0%, #1e293b 55%, #334155 100%)",
  color: "white",
  borderRadius: 24,
  boxShadow: "0 22px 52px rgba(15,23,42,0.2)",
};

const headerRow = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: 16,
  flexWrap: "wrap",
};

const eyebrow = {
  marginBottom: 8,
  color: "#86efac",
  fontSize: 11,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  fontWeight: 900,
};

const title = {
  margin: 0,
  fontSize: 30,
  letterSpacing: "-0.05em",
  lineHeight: 1.02,
};

const subtitle = {
  maxWidth: 900,
  marginTop: 10,
  color: "#cbd5e1",
  lineHeight: 1.6,
  fontSize: 13,
};

const buttonRow = {
  display: "flex",
  gap: 10,
  flexWrap: "wrap",
};

const metricGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(145px, 1fr))",
  gap: 10,
  marginTop: 20,
};

// const metricCard = {
//   padding: 13,
//   borderRadius: 16,
//   background: "rgba(255,255,255,0.1)",
//   border: "1px solid rgba(255,255,255,0.16)",
//   boxShadow: "inset 0 1px 0 rgba(255,255,255,0.08)",
// };

// const metricLabel = {
//   fontSize: 10,
//   color: "#cbd5e1",
//   fontWeight: 850,
//   textTransform: "uppercase",
//   letterSpacing: "0.08em",
// };

// const metricValue = {
//   marginTop: 7,
//   fontSize: 18,
//   fontWeight: 950,
//   letterSpacing: "-0.04em",
// };

const filterCard = {
  maxWidth: 1680,
  margin: "0 auto 18px",
  padding: 18,
  border: "1px solid #e2e8f0",
  borderRadius: 22,
  background: "rgba(255,255,255,0.9)",
  boxShadow: "0 14px 32px rgba(15,23,42,0.08)",
};

const filterHeaderRow = {
  display: "flex",
  justifyContent: "space-between",
  gap: 12,
  alignItems: "center",
  flexWrap: "wrap",
  marginBottom: 14,
};

const sectionTitle = {
  margin: 0,
  fontSize: 16,
  letterSpacing: "-0.03em",
};

const sectionSubtitle = {
  margin: "4px 0 0",
  color: "#64748b",
  fontSize: 12,
};

const resultPill = {
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  padding: "8px 12px",
  borderRadius: 999,
  background: "#f1f5f9",
  color: "#334155",
  fontSize: 11,
  border: "1px solid #e2e8f0",
};

const controls = {
  display: "flex",
  gap: 10,
  flexWrap: "wrap",
};

const searchInput = {
  minWidth: 300,
  flex: "1 1 320px",
  padding: "9px 11px",
  border: "1px solid #cbd5e1",
  borderRadius: 12,
  fontSize: 11,
  background: "white",
  outline: "none",
};

const smallInput = {
  width: 145,
  padding: "9px 11px",
  border: "1px solid #cbd5e1",
  borderRadius: 12,
  fontSize: 11,
  background: "white",
  outline: "none",
};

const selectInput = {
  minWidth: 145,
  padding: "9px 11px",
  border: "1px solid #cbd5e1",
  borderRadius: 12,
  fontSize: 11,
  background: "white",
  outline: "none",
  fontWeight: 800,
};

const button = {
  padding: "10px 14px",
  border: "1px solid #38bdf8",
  background: "#38bdf8",
  color: "#082f49",
  borderRadius: 12,
  cursor: "pointer",
  fontWeight: 850,
  boxShadow: "0 10px 20px rgba(56,189,248,0.22)",
};

const secondaryButton = {
  padding: "10px 14px",
  border: "1px solid rgba(255,255,255,0.28)",
  background: "rgba(255,255,255,0.08)",
  color: "white",
  borderRadius: 12,
  cursor: "pointer",
  fontWeight: 800,
};

const tableCard = {
  maxWidth: 1680,
  margin: "0 auto",
  padding: 18,
  border: "1px solid #e2e8f0",
  borderRadius: 22,
  background: "white",
  boxShadow: "0 16px 36px rgba(15,23,42,0.1)",
};

const tableToolbar = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 12,
  marginBottom: 14,
};

const tableWrap = {
  overflowX: "auto",
  border: "1px solid #e2e8f0",
  borderRadius: 16,
  maxHeight: "78vh",
};

const table = {
  width: "100%",
  borderCollapse: "separate",
  borderSpacing: 0,
};

const thLeft = {
  textAlign: "left",
  padding: "8px 8px",
  fontSize: 10,
  color: "#475569",
  borderBottom: "1px solid #e2e8f0",
  background: "#f8fafc",
  whiteSpace: "nowrap",
  cursor: "pointer",
  position: "sticky",
  top: 0,
  zIndex: 6,
  fontWeight: 900,
  textTransform: "uppercase",
  letterSpacing: "0.035em",
};

const thRight = {
  ...thLeft,
  textAlign: "right",
};

const td = {
  padding: "7px 8px",
  fontSize: 11,
  color: "#1e293b",
  borderBottom: "1px solid #eef2f7",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const tdRight = {
  ...td,
  textAlign: "right",
  fontVariantNumeric: "tabular-nums",
};

const rowEven = {
  background: "#ffffff",
};

const rowOdd = {
  background: "#fbfdff",
};

const informativeBuyRow = {
  background: "linear-gradient(90deg, rgba(34,197,94,0.13), rgba(255,255,255,0.96))",
};

const informativeSaleRow = {
  background: "linear-gradient(90deg, rgba(239,68,68,0.11), rgba(255,255,255,0.96))",
};

const tickerLink = {
  color: "#2563eb",
  textDecoration: "none",
  fontWeight: 900,
};

const secLink = {
  color: "#2563eb",
  textDecoration: "none",
  fontWeight: 800,
};

const sortIndicator = {
  color: "#0f172a",
  fontSize: 10,
};

const pillBase = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minWidth: 62,
  padding: "5px 8px",
  borderRadius: 999,
  fontSize: 10,
  fontWeight: 950,
  border: "1px solid transparent",
};

const buyPill = {
  color: "#047857",
  background: "#ecfdf5",
  borderColor: "#a7f3d0",
};

const salePill = {
  color: "#b91c1c",
  background: "#fff1f2",
  borderColor: "#fecdd3",
};

const informativePill = {
  color: "#065f46",
  background: "#bbf7d0",
  borderColor: "#86efac",
  boxShadow: "0 0 0 3px rgba(34,197,94,0.12)",
};

const uninformativePill = {
  color: "#64748b",
  background: "#f8fafc",
  borderColor: "#e2e8f0",
};

const neutralPill = {
  color: "#475569",
  background: "#f8fafc",
  borderColor: "#e2e8f0",
};

const message = {
  maxWidth: 1680,
  margin: "0 auto",
  padding: 20,
  color: "#475569",
  background: "white",
  borderRadius: 16,
  border: "1px solid #e2e8f0",
};

const errorBox = {
  maxWidth: 1680,
  margin: "0 auto",
  padding: 14,
  background: "#fff1f2",
  color: "#9f1239",
  border: "1px solid #fecdd3",
  borderRadius: 12,
};
